import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MailX, Undo2 } from "lucide-react";

const fmt = (iso) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");

// Hard bounces / spam complaints from the Resend delivery webhook. Flagged accounts are excluded from every audience.
export default function BroadcastBounces() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/broadcast/bounced").then(({ data }) => setData(data)).catch(() => setData({ items: [], flagged_count: 0 }));
  useEffect(() => { load(); }, []);

  const clear = async (row) => {
    if (!window.confirm(`Clear the bounce flag for ${row.email}? They will receive broadcasts again.`)) return;
    try { await api.delete(`/admin/broadcast/bounced/${row.user_id}`); toast.success("Bounce flag cleared"); load(); } catch { toast.error("Could not clear flag"); }
  };

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="broadcast-bounces">
      <div className="mb-4">
        <div className="tracking-overline text-[10px] text-[#B35C4A] inline-flex items-center gap-1.5"><MailX className="w-3 h-3" /> Bounced</div>
        <h2 className="font-serif text-2xl text-[#11211A] mt-1">Bounced addresses{data ? ` (${data.flagged_count} excluded)` : ""}</h2>
        <p className="text-xs text-[#7D8C85] mt-1">Detected automatically from the email provider's bounce and complaint events. Hard bounces and complaints are excluded from every broadcast audience until the address is fixed and the flag cleared.</p>
      </div>
      {!data ? <div className="text-sm text-[#7D8C85]">Loading…</div> : data.items.length === 0 ? <div className="text-sm text-[#7D8C85]" data-testid="bounces-empty">No bounces recorded yet.</div> : (
        <table className="w-full text-sm" data-testid="bounces-table">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#7D8C85] border-b border-[#E8ECE9]">
            <tr><th className="py-2">Email</th><th>Company</th><th>Type</th><th>Why</th><th>When</th><th></th></tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={`${r.email}-${r.created_at}`} className="border-b border-[#F0EFEA]" data-testid={`bounce-row-${r.user_id || r.email}`}>
                <td className="py-2 text-[#11211A]">{r.email}{!r.user_id && <span className="text-[10px] text-[#9AA6A0] ml-1">(no account)</span>}</td>
                <td className="py-2 text-[#7D8C85]">{r.company || "—"}</td>
                <td className="py-2"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${r.hard ? "bg-[#FBEAE6] text-[#B35C4A]" : "bg-[#F0EFEA] text-[#7D8C85]"}`}>{r.bounce_type}{r.hard ? "" : " · soft"}</span></td>
                <td className="py-2 text-xs text-[#4A5550] max-w-[320px] truncate" title={r.reason}>{r.reason || r.event}</td>
                <td className="py-2 text-[#7D8C85] tabular-nums whitespace-nowrap">{fmt(r.created_at)}</td>
                <td className="py-2 text-right">
                  {r.flagged && r.user_id && <button type="button" onClick={() => clear(r)} className="text-[11px] text-[#264839] hover:underline inline-flex items-center gap-1" data-testid={`bounce-clear-${r.user_id}`}><Undo2 className="w-3 h-3" /> Clear flag</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
